import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthState, AuthTokens, LoginCredentials, User, UserRole } from '../types/auth';
import { authService, isApiError } from '../services/authService';
import { queryClient } from '../lib/query-client';

const STORAGE_KEY = 'ediphor-auth';

// Helper to parse JWT and check expiration
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

// Helper to check if token will expire soon (within 1 minute)
export function isTokenExpiringSoon(token: string, thresholdMs = 60000): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;
    return Date.now() >= exp - thresholdMs;
  } catch {
    return true;
  }
}

interface PersistedState {
  tokens: AuthTokens | null;
  user: User | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      isSwitchingLab: false,
      error: null,

      // Login action
      login: async (credentials: LoginCredentials): Promise<boolean> => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(credentials);

          set({
            user: response.user,
            tokens: {
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
            },
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          return true;
        } catch (error) {
          const message = isApiError(error)
            ? error.response.data.message
            : 'An unexpected error occurred';

          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });

          return false;
        }
      },

      // Logout action
      logout: async (): Promise<void> => {
        const { tokens } = get();

        set({ isLoading: true });

        try {
          if (tokens?.refreshToken) {
            await authService.logout(tokens.refreshToken);
          }
        } catch {
          // Ignore logout errors - still clear local state
        } finally {
          queryClient.clear();
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      // Refresh tokens action
      refreshTokens: async (): Promise<boolean> => {
        const { tokens } = get();

        if (!tokens?.refreshToken) {
          return false;
        }

        try {
          const response = await authService.refresh(tokens.refreshToken);

          set({
            user: response.user,
            tokens: {
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
            },
            isAuthenticated: true,
            error: null,
          });

          return true;
        } catch {
          // Refresh failed - clear auth state
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            error: null,
          });

          return false;
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set tokens (used by axios interceptor after refresh)
      setTokens: (tokens: AuthTokens) => {
        set({ tokens });
      },

      switchLab: async (labId: string): Promise<void> => {
        set({ isSwitchingLab: true });
        try {
          const response = await authService.switchLab(labId);
          set({
            user: response.user,
            tokens: {
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
            },
            isSwitchingLab: false,
          });
          queryClient.invalidateQueries();
        } catch (error) {
          set({ isSwitchingLab: false });
          throw error;
        }
      },

      acknowledgeGuide: async (): Promise<void> => {
        await authService.acknowledgeGuide();
        const { user } = get();
        if (user) {
          set({ user: { ...user, guideAcknowledged: true } });
        }
      },

      // Hydrate from storage (validate tokens on app load)
      hydrateFromStorage: () => {
        const { tokens } = get();

        if (tokens?.accessToken) {
          if (isTokenExpired(tokens.accessToken)) {
            // Access token expired - try refresh
            get().refreshTokens();
          } else {
            // Token valid - mark as authenticated
            set({ isAuthenticated: true });
          }
        }
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist tokens and user, not loading/error states
      partialize: (state): PersistedState => ({
        tokens: state.tokens,
        user: state.user,
      }),
      // On rehydration, validate token
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hydrateFromStorage();
        }
      },
    }
  )
);

// Selector hooks for common use cases
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);

// Role helper hooks
export const useHasRole = (role: UserRole): boolean => {
  const user = useAuthStore((state) => state.user);
  const userRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  return userRoles.includes(role);
};

export const useHasAnyRole = (...roles: UserRole[]): boolean => {
  const user = useAuthStore((state) => state.user);
  const userRoles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
  return userRoles.some((r) => roles.includes(r));
};
